import { CAMPAIGNS_API_URL } from 'constants/ApiConstants';
import * as types from 'modules/campaigns/actionTypes';

/**
 * @param {Partial<FetchCampaignsQueryParams>} params
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchCampaignsAction}
 */
export const fetchCampaigns = (params, meta = {}) => ({
  type: types.FETCH_CAMPAIGNS,
  payload: {
    request: {
      method: 'GET',
      url: `${CAMPAIGNS_API_URL}`,
      params,
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

/**
 * @param {string} campaignId
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchCampaignAction}
 */
export const fetchCampaign = (campaignId, meta = {}) => ({
  type: types.FETCH_CAMPAIGN,
  payload: {
    request: {
      method: 'GET',
      url: `${CAMPAIGNS_API_URL}/${campaignId}`,
      params: {
        projection: 'details',
      },
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

/**
 * @param {Partial<import("../types").Campaign>} campaign
 * @param {Object} [meta={}]
 * @returns {import("../types").CreateCampaignAction}
 */
export const createCampaign = (campaign, meta = {}) => ({
  type: types.CREATE_CAMPAIGN,
  payload: {
    request: {
      method: 'POST',
      url: CAMPAIGNS_API_URL,
      data: campaign,
    },
  },
  meta,
});

/**
 * @param {Partial<import("../types").Campaign>} campaign
 * @param {Object} [meta={}]
 * @returns {import("../types").UpdateCampaignAction}
 */
export const updateCampaign = (campaign, meta = {}) => ({
  type: types.UPDATE_CAMPAIGN,
  payload: {
    request: {
      method: 'PUT',
      url: `${CAMPAIGNS_API_URL}/${campaign.id}`,
      data: campaign,
    },
  },
  meta,
});

/**
 * @param {string} campaignId
 * @param {Object} [meta={}]
 * @returns {import("../types").DeleteCampaignAction}
 */
export const deleteCampaign = (campaignId, meta = {}) => ({
  type: types.DELETE_CAMPAIGN,
  payload: {
    campaignId,
    request: {
      method: 'DELETE',
      url: `${CAMPAIGNS_API_URL}/${campaignId}`,
    },
  },
  meta,
});

/**
 * @param {string} campaignId
 * @param {Object} [meta={}]
 * @returns {import("../types").DeleteCampaignSuccessAction}
 */
export const deleteCampaignSuccess = (campaignId, meta = {}) => ({
  type: types.DELETE_CAMPAIGN_SUCCESS,
  payload: campaignId,
  meta,
});

/**
 * @param {string} campaignId
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import("../types").DeleteCampaignFailureAction}
 */
export const deleteCampaignFailure = (campaignId, error, meta = {}) => ({
  type: types.DELETE_CAMPAIGN_FAILURE,
  payload: { error, campaignId },
  error: true,
  meta,
});

/**
 * @param {string} campaignId
 * @param {import("../types").CampaignStatusEnum} status
 * @param {Object} [meta={}]
 * @returns {import("../types").PatchCampaignStatusAction}
 */
export const patchCampaignStatus = (campaignId, status, meta = {}) => ({
  type: types.PATCH_CAMPAIGN_STATUS,
  payload: {
    campaignId,
    request: {
      method: 'PATCH',
      url: `${CAMPAIGNS_API_URL}/${campaignId}`,
      data: { status }
    }
  },
  meta,
});

/**
 * @param {import("../types").Campaign} campaign
 * @param {Object} [meta={}]
 * @returns {import("../types").PatchCampaignStatusSuccessAction}
 */
export const patchCampaignStatusSuccess = (campaign, meta = {}) => ({
  type: types.PATCH_CAMPAIGN_STATUS_SUCCESS,
  payload: campaign,
  meta,
});

/**
 * @param {string} campaignId
 * @param {string} name
 * @param {Object} [meta={}]
 * @returns {import("../types").PatchCampaignNameAction}
 */
export const patchCampaignName = (campaignId, name, meta = {}) => ({
  type: types.PATCH_CAMPAIGN_NAME,
  payload: {
    campaignId,
    request: {
      method: 'PATCH',
      url: `${CAMPAIGNS_API_URL}/${campaignId}`,
      data: { name }
    }
  },
  meta
});

/**
 * @param {string} campaignId
 * @param {Object} [meta={}]
 * @returns {import("../types").DeleteCampaignSendDateAction}
 */
export const deleteCampaignSendDate = (campaignId, meta = {}) => ({
  type: types.DELETE_CAMPAIGN_SEND_DATE,
  payload: {
    request: {
      method: 'DELETE',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/sendingTime`,
    },
  },
  meta,
});

/**
 * @param {string} campaignId
 * @param {import("../types").CampaignSendDatePayload} data
 * @param {Object} [meta={}]
 * @returns {import("../types").PatchCampaignSendDateAction}
 */
export const patchCampaignSendDate = (campaignId, data, meta = {}) => ({
  type: types.PATCH_CAMPAIGN_SEND_DATE,
  payload: {
    campaignId,
    request: {
      method: 'PATCH',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/sendingTime`,
      data,
    },
  },
  meta,
});

/**
 * @param {string} campaignId
 * @param {import("../types").DuplicateCampaignData} data
 * @param {Object} [meta={}]
 * @returns {import("../types").DuplicateCampaignAction}
 */
export const duplicateCampaign = (campaignId, data, meta = {}) => ({
  type: types.DUPLICATE_CAMPAIGN,
  payload: {
    request: {
      method: 'POST',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/clone`,
      data,
    },
  },
  meta,
});

/**
 * @param {string} campaignId
 * @param {import("../types").CampaignSendDatePayload} data
 * @param {Object} [meta={}]
 * @returns {import("../types").SendCampaignAction}
 */
export const sendCampaign = (campaignId, data = {}, meta = {}) => ({
  type: types.SEND_CAMPAIGN,
  payload: {
    request: {
      method: 'POST',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/send`,
      data,
    },
  },
  meta,
});

/** @type {import("../types").OpenExportAction} */
export const openExport = (campaignId, meta = {}) => ({
  type: types.OPEN_EXPORT,
  payload: campaignId,
  meta,
});

/** @type {import("../types").CloseExportAction} */
export const closeExport = (meta = {}) => ({
  type: types.CLOSE_EXPORT,
  meta,
});

/** @type {import("../types").ExportCampaignAction} */
export const exportCampaign = (campaignId, data, meta = {}) => ({
  type: types.EXPORT_CAMPAIGN,
  payload: {
    request: {
      method: 'POST',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/export`,
      data,
    }
  },
  meta,
});
